/**
 * Shared helper for the section-level "Border Radius" control exposed on
 * every Builder section component.
 *
 * Authors get a single string-typed Builder input (e.g. `'12px'`,
 * `'24px 24px 0 0'`, `'1rem'`) that's applied to the section's outermost
 * wrapper. Defaulting to `'0px'` keeps existing pages pixel-identical — the
 * control is opt-in per section.
 *
 * Two helpers are exported so each component can pick the right shape for
 * its outermost element:
 *
 *   - `sectionBorderRadiusSx(value, { clip })` — emits an `sx`-style object
 *     for MUI components. Pass `clip: false` for sections whose children
 *     intentionally overflow the container (sticky navs with dropdowns,
 *     floating CTAs, modals/popovers triggered from inside the section).
 *     Default `clip: true` adds `overflow: 'hidden'` so background colors,
 *     gradients, and images actually round at the corners.
 *
 *   - `SECTION_BORDER_RADIUS_INPUT` — the canonical Builder.io input
 *     definition. Spread or push into each component's `inputs` array in
 *     `register-components.js` so the editor UI is identical across every
 *     section.
 */

export const sectionBorderRadiusSx = (value, { clip = true } = {}) => {
  if (!value || value === '0' || value === '0px') return null;
  return clip ? { borderRadius: value, overflow: 'hidden' } : { borderRadius: value };
};

export const SECTION_BORDER_RADIUS_INPUT = {
  name: 'sectionBorderRadius',
  friendlyName: 'Section Border Radius',
  type: 'string',
  defaultValue: '0px',
  helperText:
    "CSS border-radius applied to the section's outer container. Accepts any CSS value, e.g. '12px', '24px 24px 0 0', '1rem'.",
  advanced: true,
};
